<template>
  <!-- Calendar as the app draws it today (Workspace369 origin/main, Oct 22 2026), at the app's own scale.
       Wide containers get the desktop shell (SidebarV2 with Work open, the canvas header) around
       lib/view/schedules/desktop_calendar/desktop_calendar_widget.dart: the toolbar
       (widgets/calendar_actions_widget.dart: the month pill "October 2026", Today, Display Options, Add
       Schedule), then the week grid (week_grid/week_grid_view.dart with week_header_row.dart's "MON" over
       "19", today's number in a filled pill, time_gutter.dart's "9 AM", all_day_lane.dart, now_indicator.dart's
       red line, and event_chip.dart's chips: category colour at 8% with a 22% border, a 3px keyline, the
       title over "9:00 AM – 10:00 AM · Client"; geometry from week_grid_style.dart) or the Team view
       (week_grid/team/team_grid_view.dart: the "THU / Oct 22" gutter cell, Unassigned first with its "?"
       avatar, a column per member with a keyline, initials avatar, name and "N jobs", and drag-to-assign).
       Layout checked against the app's own render (test/visual/schedules/google_calendar_two_way_visual_check.dart).
       Below 600px it is the phone layout (mobile_calendar_widget.dart): the date bar (title, chevrons, Today,
       the view button) over the paged week, two day columns at a time, or the compact Team view. Labels are
       app_en.arb strings; Northstar Studio, its team and clients are sample data. States: `week`, `team`,
       `moved` (the unassigned Product shoot is dragged onto Leo Park's column; "Moved to …" confirms it).
       The server renders `moved`. -->
  <div class="caw" :class="`caw--${state}`" aria-hidden="true">
    <svg class="caw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`caw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="caw-desk">
      <aside class="caw-side">
        <div class="caw-side-top"><svg class="ic"><use href="#caw-view_sidebar" /></svg></div>
        <div class="caw-ws"><span class="caw-logo">N</span><span class="caw-ell">Northstar Studio</span><svg class="ic sm"><use href="#caw-unfold_more" /></svg></div>
        <nav class="caw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="caw-ni top">
              <svg class="ic"><use :href="`#caw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm caw-chev"><use :href="`#caw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="caw-ni child" :class="{ sel: c === 'Calendar' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="caw-side-foot">
          <div v-for="f in foot" :key="f.k" class="caw-ni"><svg class="ic"><use :href="`#caw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="caw-main">
        <header class="caw-head">
          <span class="caw-title"><svg class="ic sm"><use href="#caw-calendar_today" /></svg>Calendar</span>
          <span class="caw-hsearch"><svg class="ic sm"><use href="#caw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="caw-icbtn caw-bell"><svg class="ic"><use href="#caw-notifications" /></svg><b>3</b></span>
          <span class="caw-icbtn"><svg class="ic"><use href="#caw-settings" /></svg></span>
          <span class="caw-icbtn"><svg class="ic"><use href="#caw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="caw-sheet">
          <div class="caw-bar">
            <span class="caw-month"><svg class="ic"><use href="#caw-chevronLeft" /></svg><b>October 2026</b><svg class="ic"><use href="#caw-chevron_right" /></svg></span>
            <span class="caw-btn sec">Today</span>
            <span class="caw-grow" />
            <span class="caw-icbtn"><svg class="ic"><use href="#caw-search" /></svg></span>
            <span class="caw-btn sec"><svg class="ic sm"><use href="#caw-tune" /></svg>Display Options</span>
            <span class="caw-icbtn caw-sync"><svg class="ic"><use href="#caw-sync" /></svg></span>
            <span class="caw-btn"><svg class="ic sm"><use href="#caw-add" /></svg>Add Schedule</span>
          </div>

          <!-- Week -->
          <div v-if="state === 'week'" class="caw-grid caw-week">
            <div class="caw-hrow">
              <span />
              <span v-for="d in week" :key="d.n" class="caw-dh" :class="{ today: d.today }"><small>{{ d.d }}</small><b>{{ d.n }}</b></span>
            </div>
            <div class="caw-allday"><span>ALL-DAY</span><span class="caw-adc" style="grid-column: 4 / span 2">Studio offsite</span></div>
            <div class="caw-body">
              <div class="caw-gut"><span v-for="h in hours" :key="h">{{ h }}</span></div>
              <div v-for="(d, i) in week" :key="`c-${d.n}`" class="caw-col" :class="{ today: d.today }">
                <div v-for="e in weekEvents.filter((x) => x.day === i)" :key="e.t" class="caw-ev" :style="evStyle(e)">
                  <b>{{ e.t }}</b><span>{{ e.time }} · {{ e.c }}</span>
                </div>
                <div v-if="d.today" class="caw-now" :style="{ top: `${nowTop}%` }"><i /></div>
              </div>
            </div>
          </div>

          <!-- Team -->
          <div v-else class="caw-grid caw-team">
            <div class="caw-hrow">
              <span class="caw-tday"><small>THU</small><b>Oct 22</b></span>
              <span v-for="m in team" :key="m.k" class="caw-th" :style="{ '--c': m.c }">
                <i class="caw-av">{{ m.av }}</i><span><b>{{ m.k }}</b><small :key="`n-${m.k}-${jobs(m.k)}`">{{ jobs(m.k) }}</small></span>
              </span>
            </div>
            <div class="caw-body">
              <div class="caw-gut"><span v-for="h in hours" :key="h">{{ h }}</span></div>
              <div v-for="m in team" :key="`t-${m.k}`" class="caw-col">
                <div v-for="e in teamEvents.filter((x) => x.who === m.k)" :key="e.t" class="caw-ev" :class="{ moving: e.moving }" :style="evStyle(e)">
                  <b>{{ e.t }}</b><span>{{ e.time }} · {{ e.c }}</span>
                </div>
              </div>
              <div class="caw-now team" :style="{ top: `${nowTop}%` }"><i /></div>
            </div>
            <div v-if="state === 'moved'" class="caw-toast"><svg class="ic sm caw-tok"><use href="#caw-check_circle_outline" /></svg><span>Moved to Thu, Oct 22 · 1:00 PM</span><b>Undo</b><svg class="ic sm caw-tx"><use href="#caw-close" /></svg></div>
          </div>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="caw-phone">
      <div class="cap2-bar">
        <b>{{ state === 'week' ? 'Oct 19 – 25' : 'Thu, Oct 22' }}</b>
        <span class="cap2-ib"><svg class="ic"><use href="#caw-chevronLeft" /></svg></span>
        <span class="cap2-ib"><svg class="ic"><use href="#caw-chevron_right" /></svg></span>
        <span class="caw-grow" />
        <span class="cap2-pill">Today</span>
        <span class="cap2-pill"><svg class="ic"><use :href="`#caw-${state === 'week' ? 'calendar_today' : 'peopleOutline'}`" /></svg>{{ state === 'week' ? 'Week' : 'Team' }}</span>
      </div>
      <div class="caw-grid cap2-grid" :class="state === 'week' ? 'caw-week' : 'caw-team'">
        <div class="caw-hrow">
          <span />
          <template v-if="state === 'week'">
            <span v-for="d in week.slice(3, 5)" :key="`p-${d.n}`" class="caw-dh" :class="{ today: d.today }"><small>{{ d.d }}</small><b>{{ d.n }}</b></span>
          </template>
          <template v-else>
            <span v-for="m in team.slice(0, 3)" :key="`p-${m.k}`" class="caw-th" :style="{ '--c': m.c }"><i class="caw-av">{{ m.av }}</i><span><b>{{ m.k }}</b><small>{{ jobs(m.k) }}</small></span></span>
          </template>
        </div>
        <div class="caw-body">
          <div class="caw-gut"><span v-for="h in hours" :key="`ph-${h}`">{{ h }}</span></div>
          <template v-if="state === 'week'">
            <div v-for="i in [3, 4]" :key="`pc-${i}`" class="caw-col" :class="{ today: i === 3 }">
              <div v-for="e in weekEvents.filter((x) => x.day === i)" :key="e.t" class="caw-ev" :style="evStyle(e)"><b>{{ e.t }}</b><span>{{ e.time }} · {{ e.c }}</span></div>
              <div v-if="i === 3" class="caw-now" :style="{ top: `${nowTop}%` }"><i /></div>
            </div>
          </template>
          <template v-else>
            <div v-for="m in team.slice(0, 3)" :key="`pt-${m.k}`" class="caw-col">
              <div v-for="e in teamEvents.filter((x) => x.who === m.k)" :key="e.t" class="caw-ev" :class="{ moving: e.moving }" :style="evStyle(e)"><b>{{ e.t }}</b><span>{{ e.time }} · {{ e.c }}</span></div>
            </div>
            <div class="caw-now team" :style="{ top: `${nowTop}%` }"><i /></div>
          </template>
        </div>
      </div>
      <div v-if="state === 'moved'" class="caw-toast"><svg class="ic sm caw-tok"><use href="#caw-check_circle_outline" /></svg><span>Moved to Thu, Oct 22 · 1:00 PM</span><b>Undo</b><svg class="ic sm caw-tx"><use href="#caw-close" /></svg></div>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { calendarIcons } from "~/data/app-icons-calendar.js";

const props = defineProps({
  // week | team | moved
  state: { type: String, default: "moved" },
});

const usedApp = ["account_balance_wallet", "add", "auto_awesome", "calendar_today", "chat_bubble_outline", "check_circle_outline", "chevron_right", "dashboard", "expand_more", "forum", "handyman", "logout", "notifications", "people_alt", "search", "settings", "support_agent", "unfold_more", "view_sidebar", "work_outline"];
const icons = { ...Object.fromEntries(usedApp.map((k) => [k, appIcons[k]])), ...calendarIcons };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", open: true, kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];

// The default fit window, 8 AM to 6 PM (layout/time_window_resolver.dart).
const hours = ["8 AM", "9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM", "5 PM"];
const span = 10;
const nowTop = ((9 + 40 / 60 - 8) / span) * 100; // 9:40 AM, as on the Home hero

const week = [
  { d: "MON", n: 19 }, { d: "TUE", n: 20 }, { d: "WED", n: 21 }, { d: "THU", n: 22, today: true },
  { d: "FRI", n: 23 }, { d: "SAT", n: 24 }, { d: "SUN", n: 25 },
];
const BLUE = "#2563eb", GREEN = "#16a34a", ORANGE = "#f97316", PURPLE = "#9333ea", TEAL = "#0891b2";
const weekEvents = [
  { day: 0, s: 9, e: 10, t: "Discovery call", time: "9:00 AM – 10:00 AM", c: "Brightside Dental", col: BLUE },
  { day: 0, s: 13, e: 15, t: "Menu photo edit", time: "1:00 PM – 3:00 PM", c: "Juniper Bakery", col: PURPLE },
  { day: 1, s: 10, e: 11.5, t: "Brand workshop", time: "10:00 AM – 11:30 AM", c: "Marlowe Coffee Co.", col: GREEN },
  { day: 1, s: 14, e: 15, t: "Proposal review", time: "2:00 PM – 3:00 PM", c: "Orchard Veterinary", col: BLUE },
  { day: 2, s: 8.5, e: 9.5, t: "Site visit", time: "8:30 AM – 9:30 AM", c: "Orchard Veterinary", col: TEAL },
  { day: 2, s: 11, e: 12, t: "Retainer check-in", time: "11:00 AM – 12:00 PM", c: "Atlas Logistics", col: BLUE },
  { day: 3, s: 8, e: 9, t: "Team stand-up", time: "8:00 AM – 9:00 AM", c: "Northstar Studio", col: ORANGE },
  { day: 3, s: 11, e: 12, t: "Send brand guidelines", time: "11:00 AM – 12:00 PM", c: "Juniper Bakery", col: GREEN },
  { day: 3, s: 13, e: 15.5, t: "Product shoot", time: "1:00 PM – 3:30 PM", c: "Marlowe Coffee Co.", col: PURPLE },
  { day: 4, s: 9, e: 10.5, t: "Logo presentation", time: "9:00 AM – 10:30 AM", c: "Marlowe Coffee Co.", col: GREEN },
  { day: 4, s: 15, e: 16, t: "Invoice follow-up", time: "3:00 PM – 4:00 PM", c: "Harbor & Co.", col: BLUE },
];

const team = [
  { k: "Unassigned", av: "?", c: "#bdbdbd" },
  { k: "Maya Chen", av: "MC", c: "#2563eb" },
  { k: "Leo Park", av: "LP", c: "#16a34a" },
  { k: "Priya Shah", av: "PS", c: "#9333ea" },
  { k: "Jordan Ruiz", av: "JR", c: "#f97316" },
];
const teamEvents = computed(() => {
  const moved = props.state === "moved";
  return [
    { who: moved ? "Leo Park" : "Unassigned", s: 13, e: 15.5, t: "Product shoot", time: "1:00 PM – 3:30 PM", c: "Marlowe Coffee Co.", col: PURPLE, moving: moved },
    { who: "Maya Chen", s: 8, e: 9, t: "Team stand-up", time: "8:00 AM – 9:00 AM", c: "Northstar Studio", col: ORANGE },
    { who: "Maya Chen", s: 11, e: 12, t: "Send brand guidelines", time: "11:00 AM – 12:00 PM", c: "Juniper Bakery", col: GREEN },
    { who: "Leo Park", s: 8, e: 9, t: "Team stand-up", time: "8:00 AM – 9:00 AM", c: "Northstar Studio", col: ORANGE },
    { who: "Leo Park", s: 10, e: 11.5, t: "Lighting test", time: "10:00 AM – 11:30 AM", c: "Marlowe Coffee Co.", col: PURPLE },
    { who: "Priya Shah", s: 9, e: 10.5, t: "Appointment page copy", time: "9:00 AM – 10:30 AM", c: "Orchard Veterinary", col: TEAL },
    { who: "Priya Shah", s: 14, e: 15, t: "Proposal review", time: "2:00 PM – 3:00 PM", c: "Brightside Dental", col: BLUE },
    { who: "Jordan Ruiz", s: 8.5, e: 9.5, t: "Menu launch call", time: "8:30 AM – 9:30 AM", c: "Juniper Bakery", col: GREEN },
    { who: "Jordan Ruiz", s: 12, e: 13, t: "Courier pickup", time: "12:00 PM – 1:00 PM", c: "Juniper Bakery", col: BLUE },
  ];
});
const jobs = (who) => {
  const n = teamEvents.value.filter((e) => e.who === who).length;
  return n === 1 ? "1 job" : `${n} jobs`;
};
const evStyle = (e) => ({ top: `${((e.s - 8) / span) * 100}%`, height: `${((e.e - e.s) / span) * 100}%`, "--c": e.col });
</script>

<style>
/* Design tokens: shadcn Neutral (the app's default scheme) as in InvoicesAppWindow; chips and the now-line from
   week_grid_style.dart and now_indicator.dart. */
.caw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef; --green-t: #2d7a4e;
  --ink2: rgb(10 10 10 / 0.72); --ink3: rgb(10 10 10 / 0.55); --selected: rgb(10 10 10 / 0.06);
  --hair: rgb(10 10 10 / 0.08); --wash: rgb(23 23 23 / 0.03); --lane: rgb(10 10 10 / 0.025); --chip-a: 8%;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .caw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525; --green-t: #6ed39a;
  --ink2: rgb(250 250 250 / 0.72); --ink3: rgb(250 250 250 / 0.55); --selected: rgb(250 250 250 / 0.1);
  --hair: rgb(250 250 250 / 0.1); --wash: rgb(250 250 250 / 0.03); --lane: rgb(250 250 250 / 0.03); --chip-a: 14%;
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.caw { min-height: 640px; }
@media (max-width: 1128px) { .caw { min-height: 560px; } }
@media (max-width: 648px) { .caw { min-height: 0; aspect-ratio: 390 / 700; } }
.caw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.caw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.caw .ic.sm { width: 16px; height: 16px; }
.caw-grow { flex: 1; }
.caw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ---------- desktop shell (as InvoicesAppWindow) ---------- */
.caw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 640px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
}
.dark .caw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .caw-phone { border-color: #3a3a3a; }
.caw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.caw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.caw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.caw-ws .ic { margin-left: auto; color: var(--ink3); }
.caw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .caw-logo { background: #fafafa; color: #171717; }
.caw-nav { flex: 1; min-height: 0; overflow: hidden; }
.caw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.caw-ni.top { color: var(--ink); font-weight: 600; }
.caw-ni.child { padding-left: 38px; }
.caw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 600; }
.caw-ni .caw-chev { margin-left: auto; color: var(--ink3); }
.caw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.caw-side-foot .caw-ni { height: 30px; }
.caw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.caw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.caw-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.caw-title .ic { color: var(--ink2); }
.caw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.caw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.caw-icbtn { position: relative; display: grid; flex: none; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.caw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.caw-sheet { display: flex; flex-direction: column; min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* ---------- Calendar ---------- */
.caw-bar { display: flex; flex: none; align-items: center; gap: 8px; padding: 10px 12px; }
.caw-month { display: inline-flex; align-items: center; justify-content: space-between; width: 220px; height: 36px; border: 1px solid rgb(from var(--ink) r g b / 0.18); border-radius: 8px; padding: 0 6px; }
.caw-month b { font-size: 12px; font-weight: 600; }
.caw-btn { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 32px; border: 1px solid transparent; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; }
.caw-btn.sec { border-color: var(--card-edge); background: var(--page); color: var(--ink); }
.caw-grid { position: relative; display: flex; flex: 1; flex-direction: column; min-height: 0; border-top: 1px solid var(--hair); }
.caw-hrow { display: grid; flex: none; height: 52px; border-bottom: 1px solid var(--hair); }
.caw-week .caw-hrow, .caw-week .caw-body { grid-template-columns: 52px repeat(7, minmax(0, 1fr)); }
.caw-team .caw-hrow, .caw-team .caw-body { grid-template-columns: 52px repeat(5, minmax(0, 1fr)); }
.caw-dh { display: grid; place-content: center; justify-items: center; gap: 2px; }
.caw-dh small { color: var(--ink3); font-size: 10px; letter-spacing: 0.06em; }
.caw-dh b { display: grid; place-items: center; min-width: 26px; height: 22px; border-radius: 6px; font-size: 15px; font-weight: 600; }
.caw-dh.today b { background: var(--ink); color: var(--page); }
.caw-allday { display: grid; flex: none; grid-template-columns: 52px repeat(7, minmax(0, 1fr)); align-items: center; height: 26px; border-bottom: 1px solid var(--hair); background: var(--lane); }
.caw-allday > span:first-child { padding-left: 6px; color: var(--ink3); font-size: 9px; letter-spacing: 0.04em; }
.caw-adc { margin: 0 3px; border: 1px solid rgb(249 115 22 / 0.3); border-radius: 5px; background: rgb(249 115 22 / 0.08); padding: 1px 6px; font-size: 12px; font-weight: 600; }
.caw-body { position: relative; display: grid; flex: 1; min-height: 0; background-image: linear-gradient(var(--hair) 1px, transparent 1px); background-position: 0 -1px; background-size: 100% 10%; }
.caw-gut { position: relative; display: grid; grid-template-rows: repeat(10, 1fr); }
.caw-gut span { padding: 3px 6px 0 0; color: var(--ink3); font-size: 10px; text-align: right; }
.caw-col { position: relative; border-left: 1px solid var(--hair); }
.caw-col.today { background: var(--wash); }
.caw-ev { position: absolute; left: 2px; right: 3px; display: grid; align-content: start; gap: 1px; overflow: hidden; border: 1px solid color-mix(in srgb, var(--c) 22%, transparent); border-left: 3px solid var(--c); border-radius: 6px; background: color-mix(in srgb, var(--c) var(--chip-a), var(--page)); padding: 4px 6px 3px 6px; }
.caw-ev b { overflow: hidden; font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.caw-ev span { overflow: hidden; color: var(--ink3); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.caw-ev.moving { animation: caw-drop 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both; box-shadow: 0 0 0 2px color-mix(in srgb, var(--c) 30%, transparent); }
.caw-now { position: absolute; z-index: 2; left: 0; right: 0; height: 2px; background: #dc2626; pointer-events: none; }
.caw-now.team { left: 52px; }
.caw-now i { position: absolute; top: -2.5px; left: -3.5px; width: 7px; height: 7px; border-radius: 50%; background: #dc2626; }
.caw-tday { display: grid; place-content: center; justify-items: center; }
.caw-tday small { color: var(--ink3); font-size: 10px; letter-spacing: 0.06em; }
.caw-tday b { font-size: 11px; font-weight: 600; }
.caw-th { display: flex; align-items: center; gap: 8px; min-width: 0; border-left: 1px solid var(--hair); box-shadow: inset 0 3px 0 var(--c); padding: 0 10px; }
.caw-th > span { display: grid; min-width: 0; }
.caw-th b { overflow: hidden; font-size: 11px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.caw-th small { color: var(--ink3); font-size: 10px; }
.caw-av { display: grid; flex: none; place-items: center; width: 24px; height: 24px; border-radius: 50%; background: var(--c); color: #fff; font-size: 9px; font-style: normal; font-weight: 700; }
.caw-toast { position: absolute; z-index: 4; right: 16px; bottom: 16px; display: flex; align-items: center; gap: 8px; width: 320px; max-width: calc(100% - 32px); border: 1px solid var(--card-edge); border-radius: 8px; background: var(--page); padding: 8px 8px 8px 12px; color: var(--ink); font-size: 14px; font-weight: 500; box-shadow: 2px 4px 16px rgb(0 0 0 / 0.16), 0 2px 4px rgb(0 0 0 / 0.08); animation: caw-toast 0.4s ease 0.4s both; }
.caw-toast span { flex: 1; min-width: 0; padding: 4px 0; }
.caw-toast b { padding: 0 4px; color: var(--primary); font-weight: 600; }
/* Desktop is the app's compact density (app_toaster.dart): 13px message, 6px padding, 4px before Undo, 2px before ×. */
.caw-desk .caw-toast { gap: 0; padding: 6px 6px 6px 12px; font-size: 13px; }
.caw-desk .caw-toast span { margin-left: 8px; }
.caw-desk .caw-toast b { margin-left: 4px; padding: 5px 8px; }
.caw-desk .caw-tx { box-sizing: content-box; margin-left: 2px; padding: 6px; }
.caw-tok { color: var(--green-t); }
.caw-tx { color: var(--ink3); }
@keyframes caw-drop { from { transform: translate(-60%, -4px) scale(1.02); opacity: 0.6; } }
@keyframes caw-toast { from { opacity: 0; transform: translateY(8px); } }
@keyframes caw-toastp { from { opacity: 0; transform: translateY(-8px); } }

/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it. */
@container (max-width: 1080px) {
  .caw-desk { grid-template-columns: minmax(0, 1fr); height: 560px; }
  .caw-side, .caw-hsearch, .caw-sync { display: none; }
  .caw-head { padding-left: 14px; }
  .caw-month { width: 184px; }
}
@container (max-width: 860px) {
  .caw-bar > .caw-icbtn, .caw-bar .caw-btn.sec:nth-of-type(2) { display: none; }
  .caw-ev span { display: none; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.caw-phone { display: none; }
@container (max-width: 600px) {
  .caw-desk { display: none; }
  .caw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: flex;
    flex-direction: column;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--page);
    font-size: calc(var(--t) * 13);
  }
  .caw-phone .ic { width: calc(var(--t) * 15); height: calc(var(--t) * 15); }
  .cap2-bar { display: flex; flex: none; align-items: center; gap: calc(var(--u) * 4); padding: calc(var(--u) * 10) calc(var(--u) * 12); }
  .cap2-bar b { margin-right: calc(var(--u) * 4); font-size: calc(var(--t) * 14); font-weight: 800; white-space: nowrap; }
  .cap2-ib { display: grid; place-items: center; width: calc(var(--u) * 34); height: calc(var(--u) * 34); }
  .cap2-pill { display: inline-flex; align-items: center; gap: calc(var(--u) * 4); height: calc(var(--u) * 32); border: 1px solid var(--card-edge); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 9); font-size: calc(var(--t) * 12); font-weight: 500; white-space: nowrap; }
  .cap2-grid.caw-week .caw-hrow, .cap2-grid.caw-week .caw-body { grid-template-columns: calc(var(--u) * 44) repeat(2, minmax(0, 1fr)); }
  .cap2-grid.caw-team .caw-hrow, .cap2-grid.caw-team .caw-body { grid-template-columns: calc(var(--u) * 44) repeat(3, minmax(0, 1fr)); }
  .caw-phone .caw-hrow { height: calc(var(--u) * 52); }
  .caw-phone .caw-dh small, .caw-phone .caw-gut span, .caw-phone .caw-th small { font-size: calc(var(--t) * 9); }
  .caw-phone .caw-dh b { font-size: calc(var(--t) * 15); }
  .caw-phone .caw-th { gap: calc(var(--u) * 5); padding: 0 calc(var(--u) * 5); }
  .caw-phone .caw-th b { font-size: calc(var(--t) * 10); }
  .caw-phone .caw-av { width: calc(var(--u) * 22); height: calc(var(--u) * 22); font-size: calc(var(--u) * 8); }
  .caw-phone .caw-ev b { font-size: calc(var(--t) * 11); }
  .caw-phone .caw-ev span { font-size: calc(var(--t) * 9); }
  .caw-phone .caw-now.team { left: calc(var(--u) * 44); }
  .caw-phone .caw-toast { top: calc(var(--u) * 12); right: calc(var(--u) * 12); bottom: auto; left: calc(var(--u) * 12); width: auto; max-width: none; gap: calc(var(--u) * 8); padding: calc(var(--u) * 8) calc(var(--u) * 8) calc(var(--u) * 8) calc(var(--u) * 12); font-size: calc(var(--t) * 12); animation-name: caw-toastp; }
}
@media (prefers-reduced-motion: reduce) { .caw * { animation: none !important; transition: none !important; } }
</style>
